/**
 * Process the store list response from the API
 * @param {Object} response - API response
 * @returns {Object} - Processed response with formatted data
 */
export const storeListGet = (response) => {
  if (!response || !response.data) {
    return {
      success: false,
      message: 'Failed to fetch store list',
      data: [],
      total_count: 0
    };
  }

  try {
    const { data } = response;

    // Log the raw data to debug pagination issues
    // console.log('Raw API response data:', data);

    return {
      success: true,
      message: data.message || 'Store list fetched successfully',
      data: Array.isArray(data.data) ? data.data.map(store => ({
        id: store.id || null,
        slack: store.slack || '',
        store_code: store.store_code || '',
        name: store.name || '',
        email: store.email || '',
        phone: store.phone || '',
        city: store.city || '',
        tax_number: store.tax_number || null,
        address: store.address || '',
        pincode: store.pincode || null,
        primary_contact: store.primary_contact || '',
        secondary_contact: store.secondary_contact || null,
        primary_email: store.primary_email || '',
        secondary_email: store.secondary_email || null,
        country: store.country ? {
          id: store.country.id || null,
          name: store.country.name || '',
          code: store.country.code || ''
        } : null,
        tax_code: store.tax_code || null,
        discount_code: store.discount_code || null,
        status: store.status ?? 0,
        status_data: store.status_data ? {
          id: store.status_data.id || null,
          key: store.status_data.key || '',
          value: store.status_data.value || '',
          value_constant: store.status_data.value_constant || '',
          label: store.status_data.label || '',
          color: store.status_data.color || '',
          status: store.status_data.status ?? 0
        } : null,
        invoice_type: store.invoice_type || '',
        currency_code: store.currency_code || '',
        currency_name: store.currency_name || null,
        printnode_enabled: store.printnode_enabled ?? 0,
        invoice_printer: store.invoice_printer || null,
        org_id: store.org_id || null,
        tax_code_id: store.tax_code_id || null,
        discount_code_id: store.discount_code_id || null,
        country_id: store.country_id || null,
        state_code: store.state_code || '',
        state: store.state || '',
        restaurant_mode: store.restaurant_mode ?? 0,
        restaurant_chef_role_id: store.restaurant_chef_role_id || null,
        restaurant_waiter_role_id: store.restaurant_waiter_role_id || null,
        restaurant_billing_type_id: store.restaurant_billing_type_id || null,
        enable_customer_popup: store.enable_customer_popup ?? 0,
        enable_variants_popup: store.enable_variants_popup ?? 0,
        digital_menu_enabled: store.digital_menu_enabled ?? 0,
        enable_digital_menu_otp_verification: store.enable_digital_menu_otp_verification ?? 0,
        digital_menu_send_order_to_kitchen: store.digital_menu_send_order_to_kitchen ?? 0,
        menu_language_id: store.menu_language_id || null,
        menu_open_time: store.menu_open_time || null,
        menu_close_time: store.menu_close_time || null,
        printnode_api_key: store.printnode_api_key || null,
        pos_printer_id: store.pos_printer_id || null,
        bill_printer_id: store.bill_printer_id || null,
        kot_printer_id: store.kot_printer_id || null,
        other_printer_id: store.other_printer_id || null,
        invoice_format: store.invoice_format || '',
        invoice_sequence_digits: store.invoice_sequence_digits || '',
        invoice_start_number: store.invoice_start_number || '',
        invoice_reset_sequence: store.invoice_reset_sequence || '',
        createdAt: store.createdAt || '',
        updatedAt: store.updatedAt || '',
        created_by: store.created_by && typeof store.created_by === 'object' ? {
          id: store.created_by.id || null,
          username: store.created_by.username || '',
          email: store.created_by.email || ''
        } : null,
        updated_by: store.updated_by && typeof store.updated_by === 'object' ? {
          id: store.updated_by.id || null,
          username: store.updated_by.username || '',
          email: store.updated_by.email || ''
        } : null,
        created_by_name: store.created_by?.username || null,
        meta_data: Array.isArray(store.meta_data) ? store.meta_data : [],
        receipt_note: store.receipt_note || null,
        max_business_hours: store.max_business_hours || '',
        is_primary: store.is_primary ?? false,
        parent_store_id: store.parent_store_id || null,
        store_type: store.store_type || '',
        is_procurement_hub: store.is_procurement_hub ?? 0,
        can_sell: store.can_sell ?? false,
        feature_flags: store.feature_flags || null,
        menu_json: store.menu_json || null,
        bill_printer_config: store.bill_printer_config || null,
        kot_printer_config: store.kot_printer_config || null,
        orgMetaStoreRelation: store.orgMetaStoreRelation ? {
          id: store.orgMetaStoreRelation.id || null,
          org_id: store.orgMetaStoreRelation.org_id || null,
          allow_main_store: store.orgMetaStoreRelation.allow_main_store ?? false
        } : null,
        is_pms_connected: store.is_pms_connected ?? 0,
        child_stores: Array.isArray(store.child_stores) ? store.child_stores.map(cs => ({
          id: cs.id || null,
          slack: cs.slack || '',
          store_code: cs.store_code || '',
          name: cs.name || '',
          tax_number: cs.tax_number || null,
          address: cs.address || '',
          pincode: cs.pincode || null,
          primary_contact: cs.primary_contact || '',
          secondary_contact: cs.secondary_contact || null,
          primary_email: cs.primary_email || '',
          secondary_email: cs.secondary_email || null,
          country: cs.country ? {
            id: cs.country.id || null,
            name: cs.country.name || '',
            code: cs.country.code || ''
          } : null,
          tax_code: cs.tax_code || null,
          discount_code: cs.discount_code || null,
          status: cs.status ?? 0,
          status_data: cs.status_data ? {
            id: cs.status_data.id || null,
            key: cs.status_data.key || '',
            value: cs.status_data.value || '',
            value_constant: cs.status_data.value_constant || '',
            label: cs.status_data.label || '',
            color: cs.status_data.color || '',
            status: cs.status_data.status ?? 0
          } : null,
          invoice_type: cs.invoice_type || '',
          currency_code: cs.currency_code || '',
          currency_name: cs.currency_name || null,
          printnode_enabled: cs.printnode_enabled ?? 0,
          org_id: cs.org_id || null,
          tax_code_id: cs.tax_code_id || null,
          discount_code_id: cs.discount_code_id || null,
          country_id: cs.country_id || null,
          state_code: cs.state_code || '',
          state: cs.state || '',
          restaurant_mode: cs.restaurant_mode ?? 0,
          restaurant_chef_role_id: cs.restaurant_chef_role_id || null,
          restaurant_waiter_role_id: cs.restaurant_waiter_role_id || null,
          restaurant_billing_type_id: cs.restaurant_billing_type_id || null,
          enable_customer_popup: cs.enable_customer_popup ?? 0,
          enable_variants_popup: cs.enable_variants_popup ?? 0,
          digital_menu_enabled: cs.digital_menu_enabled ?? 0,
          enable_digital_menu_otp_verification: cs.enable_digital_menu_otp_verification ?? 0,
          digital_menu_send_order_to_kitchen: cs.digital_menu_send_order_to_kitchen ?? 0,
          menu_language_id: cs.menu_language_id || null,
          menu_open_time: cs.menu_open_time || null,
          menu_close_time: cs.menu_close_time || null,
          printnode_api_key: cs.printnode_api_key || null,
          pos_printer_id: cs.pos_printer_id || null,
          bill_printer_id: cs.bill_printer_id || null,
          kot_printer_id: cs.kot_printer_id || null,
          other_printer_id: cs.other_printer_id || null,
          invoice_format: cs.invoice_format || '',
          invoice_sequence_digits: cs.invoice_sequence_digits || '',
          invoice_start_number: cs.invoice_start_number || '',
          invoice_reset_sequence: cs.invoice_reset_sequence || '',
          createdAt: cs.createdAt || '',
          updatedAt: cs.updatedAt || '',
          created_by: cs.created_by && typeof cs.created_by === 'object' ? {
            id: cs.created_by.id || null,
            username: cs.created_by.username || '',
            email: cs.created_by.email || ''
          } : null,
          updated_by: cs.updated_by && typeof cs.updated_by === 'object' ? {
            id: cs.updated_by.id || null,
            username: cs.updated_by.username || '',
            email: cs.updated_by.email || ''
          } : null,
          meta_data: Array.isArray(cs.meta_data) ? cs.meta_data : [],
          receipt_note: cs.receipt_note || null,
          max_business_hours: cs.max_business_hours || '',
          is_primary: cs.is_primary ?? false,
          parent_store_id: cs.parent_store_id || null,
          store_type: cs.store_type || '',
          is_procurement_hub: cs.is_procurement_hub ?? 0,
          can_sell: cs.can_sell ?? false,
          feature_flags: cs.feature_flags || null,
          menu_json: cs.menu_json || null,
          bill_printer_config: cs.bill_printer_config || null,
          kot_printer_config: cs.kot_printer_config || null,
          self_procurement: store?.self_procurement || false,
          orgMetaStoreRelation: cs.orgMetaStoreRelation ? {
            id: cs.orgMetaStoreRelation.id || null,
            org_id: cs.orgMetaStoreRelation.org_id || null,
            allow_main_store: cs.orgMetaStoreRelation.allow_main_store ?? false
          } : null,
          is_pms_connected: cs.is_pms_connected ?? 0
        })) : []
      })) : [],
      // Check for total count in different possible locations in the API response
      total_count: data.total_count || data.total || (Array.isArray(data.data) ? data.data.length : 0) || 0
    };
  } catch (error) {
    console.error('Error processing store list:', error);
    return {
      success: false,
      message: 'Error processing store list',
      data: [],
      total_count: 0
    };
  }
};



/**
 * Process the store details response from the API
 * @param {Object} response - API response
 * @returns {Object} - Processed response with formatted data
 */
export const storeDetailsGet = (response) => {
  if (!response || !response.data) {
    return {
      success: false,
      message: 'Failed to fetch store details',
      data: null
    };
  }

  try {
    const { data } = response;

    if (!data.data) {
      return {
        success: false,
        message: data.message || 'Store details not found',
        data: null
      };
    }

    const store = data.data;
    // console.log("store---",store)
    return {
      success: true,
      message: data.message || 'Store details fetched successfully',
      data: {
        // Basic information
        store_code: store.store_code || '',
        slack: store.slack || '',
        name: store.name || '',
        tax_number: store.tax_number || '',
        receipt_note: store.receipt_note || '',
        // Contact information
        primary_contact: store.primary_contact || '',
        secondary_contact: store.secondary_contact || '',
        primary_email: store.primary_email || store.email || '',
        secondary_email: store.secondary_email || '',

        // Address information
        address: store.address || '',
        pincode: store.pincode || '',

        // Country information - can be object or direct ID
        country: store.country || {},
        country_id: store.country?.id || store.country_id || '',
        state: store.state || '',
        state_code: store.state_code || '',

        // Status
        status: store.status || 0,
        status_data: store.status_data || {},

        // Restaurant mode settings
        restaurant_mode: store.restaurant_mode || 0,
        restaurant_billing_type_id: store.restaurant_billing_type_id || '',
        restaurant_waiter_role_id: store.restaurant_waiter_role_id || '',
        restaurant_chef_role_id: store.restaurant_chef_role_id || '',

        // Digital menu settings
        digital_menu_enabled: store.digital_menu_enabled || 0,
        menu_open_time: store.menu_open_time || '',
        menu_close_time: store.menu_close_time || '',
        enable_digital_menu_otp_verification: store.enable_digital_menu_otp_verification || 0,
        digital_menu_send_order_to_kitchen: store.digital_menu_send_order_to_kitchen || 0,
        menu_language_id: store.menu_language_id || '',

        // Currency and tax settings
        currency_code: store.currency_code || '',
        currency_name: store.currency_name || '',
        tax_code_id: store.tax_code_id || '',
        tax_code: store.tax_code || null,
        discount_code_id: store.discount_code_id || '',
        discount_code: store.discount_code || null,

        // POS screen settings
        enable_customer_popup: store.enable_customer_popup || 0,
        enable_variants_popup: store.enable_variants_popup || 0,

        // Invoice settings
        invoice_type: store.invoice_type || '',

        // Print settings
        printnode_enabled: store.printnode_enabled || 0,
        printnode_api_key: store.printnode_api_key || '',
        pos_printer_id: store.pos_printer_id || '',
        kot_printer_id: store.kot_printer_id || '',
        other_printer_id: store.other_printer_id || '',
        bill_printer_id: store.bill_printer_id || '',

        // KOT and Bill Printer Information
        kot_printer_config: store.kot_printer_config || null,

        bill_printer_config: store.bill_printer_config || null,


        // Metadata
        meta_data: store.meta_data || [],
        id: store.id,
        org_id: store.org_id,
        createdAt: store.createdAt,
        updatedAt: store.updatedAt,
        created_by: store.created_by || {},
        created_by_name: store.created_by?.username || '',
        updated_by: store.updated_by || null,

        invoice_format: store?.invoice_format || '',
        invoice_sequence_digits: store?.invoice_sequence_digits || '',
        invoice_start_number: store?.invoice_start_number || '',
        invoice_reset_sequence: store?.invoice_reset_sequence || '',

        max_business_hours: store?.max_business_hours || '',

        bill_printer_config: store?.bill_printer_config || '',
        kot_printer_config: store?.kot_printer_config || '',

        is_primary: store?.is_primary ? true : false,
        currecncy_code: store?.currecncy_code || '',
        can_sell: store?.can_sell || false,
        store_type: store?.store_type || '',
        parent_store_id: store?.parent_store_id || null,
        parent_store: store?.parent_store || null,
        parent_store_name: store?.parent_store_name || '',
        parent_store_code: store?.parent_store_code || '',
        is_procurement_hub: store?.is_procurement_hub || false,
        is_pms_connected: store?.is_pms_connected || false,
        self_procurement: store?.self_procurement || false,
      }
    };
  } catch (error) {
    console.error('Error processing store details:', error);
    return {
      success: false,
      message: 'Error processing store details',
      data: null
    };
  }
};

/**
 * Process the create store response from the API
 * @param {Object} response - API response
 * @returns {Object} - Processed response with success status
 */
export const storeCreatePost = (response) => {
  if (!response || !response.data) {
    return {
      success: false,
      message: 'Failed to create store',
      data: null
    };
  }

  try {
    const { data } = response;

    return {
      success: data.status || false,
      message: data.message || 'Store created successfully',
      data: data.data || null
    };
  } catch (error) {
    console.error('Error processing store creation:', error);
    return {
      success: false,
      message: 'Error processing store creation',
      data: null
    };
  }
};

/**
 * Process the update store response from the API
 * @param {Object} response - API response
 * @returns {Object} - Processed response with success status
 */
export const storeUpdatePut = (response) => {
  if (!response || !response.data) {
    return {
      success: false,
      message: 'Failed to update store',
      data: null
    };
  }

  try {
    const { data } = response;

    return {
      success: data.status || false,
      message: data.message || 'Store updated successfully',
      data: data.data || null
    };
  } catch (error) {
    console.error('Error processing store update:', error);
    return {
      success: false,
      message: 'Error processing store update',
      data: null
    };
  }
};

/**
 * Process the delete store response from the API
 * @param {Object} response - API response
 * @returns {Object} - Processed response with success status
 */
export const storeDeleteDel = (response) => {
  if (!response || !response.data) {
    return {
      success: false,
      message: 'Failed to delete store',
      data: null
    };
  }

  try {
    const { data } = response;

    return {
      success: data.status || false,
      message: data.message || 'Store deleted successfully',
      data: null
    };
  } catch (error) {
    console.error('Error processing store deletion:', error);
    return {
      success: false,
      message: 'Error processing store deletion',
      data: null
    };
  }
};

/**
 * Process the update store status response from the API
 * @param {Object} response - API response
 * @returns {Object} - Processed response with success status
 */
export const storeStatusPut = (response) => {
  if (!response || !response.data) {
    return {
      success: false,
      message: 'Failed to update store status',
      data: null
    };
  }

  try {
    const { data } = response;

    return {
      success: data.status || false,
      message: data.message || 'Store status updated successfully',
      data: data.data || null
    };
  } catch (error) {
    console.error('Error processing store status update:', error);
    return {
      success: false,
      message: 'Error processing store status update',
      data: null
    };
  }
};

/**
 * Process generic API response
 * @param {Object} response - API response from generic endpoint
 * @returns {Object} Formatted response data
 */
export const genericApiGet = (response, model) => {
  if (response && response.data && response.data.success === true) {
    // Map the API response to our frontend model
    const items = Array.isArray(response.data.data) ? response.data.data[0][`${model}_data`] : [];

    return {
      success: true,
      data: items
    };
  }
  return {
    success: false,
    message: response?.data?.message || 'Failed to fetch data',
    data: []
  };
};
